import React, { useMemo } from 'react';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { getCo2eChartDataFromResults } from '@ui/BarChart/helpers/bar-chart.helpers';
import { useResolvedProjectTarget } from '@features/scope/scope-targets.hooks';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import { useSelectedElementId, useSelectedVersion } from '@client/store/ui';
import {
  useSelectedProposalId,
  useSetSelectedProposalId,
} from '@client/hooks/use-selected-proposal-id.hooks';
import { useMouseEventCallback } from '@client/hooks/use-events.hooks';
import {
  elementListUrlPageFromPage,
  usePageFromUrl,
  useProjectNavigation,
} from '@client/hooks/use-router.hooks';
import { URL_CATEGORY_NONE } from '@client/models/router.model';
import { useProposalsResultRecords } from '../use-proposals.hooks';
import { useResultsPerGFA } from '@features/results/use-results.hooks';
import { Results } from '@shared/models/unit.model';
import { OverviewBarShell } from '@features/results/OverviewBarShell';
import {
  applyElementSelectionForProposalNavigation,
  hasElementSelection,
  resolveElementSelectionForProposalNavigation,
} from '../helpers/proposal-selection.helpers';
import {
  hasVisibleProposalTarget,
  PROPOSALS_OVERVIEW_BASELINE_BAR_COLOR,
} from '../proposals-overview-bar-layout.helpers';

type BaselineBarProps = {
  barWidth: number;
  maxBarHeight: number;
  /** Shared with {@link ProposalBar} — max kg CO₂e/m² GFA for bar height scale. */
  co2ePerGfaMax: number;
  /** Shared with {@link ProposalBar} — max SEK/m² GFA for cost column scale. */
  costPerGfaMax: number;
  /** Coordinated across the overview so clouds are either shown on every bar or none. */
  showCo2LabelIcon: boolean;
  /** When the bar is already selected and clicked — clears group selection in the element list (same as {@link ProposalBar}). */
  onDeselectGroup?: () => void;
};

/**
 * Bar representing the baseline (no proposal applied) in the
 * {@link ProposalsOverview} chart. Selecting the baseline clears the
 * URL-backed proposal selection. A second click while baseline is already
 * active clears the selected element (same pattern as {@link ProposalBar}).
 */
const BaselineBar: React.FC<BaselineBarProps> = ({
  barWidth,
  maxBarHeight,
  co2ePerGfaMax,
  costPerGfaMax,
  showCo2LabelIcon,
  onDeselectGroup,
}) => {
  const target = useResolvedProjectTarget();
  const displayCurrency = useDisplayCurrency();
  const selectedProposalId = useSelectedProposalId();
  const setSelectedProposalId = useSetSelectedProposalId();
  const navigateTo = useProjectNavigation();
  const pageFromUrl = usePageFromUrl();
  const selectedElementId = useSelectedElementId();
  const isSelected = selectedProposalId === undefined;

  const version = useSelectedVersion();
  // Lifecycle-filtered records for all proposals + baseline (baseline is
  // stored under `version.id`). Reading `version.resultsRecord` directly
  // would skip the LifecycleSelect filter, matching the bug had on the
  // proposal bars themselves.
  const proposalsResultRecords = useProposalsResultRecords();
  // proposalsResultRecords[version.id] is the baseline ResultsRecord (keyed by
  // element id); the version-level totals live under [version.id][version.id].
  const baselineResults: Results = useMemo(
    () =>
      version ? (proposalsResultRecords[version.id]?.[version.id] ?? {}) : {},
    [version, proposalsResultRecords],
  );
  const baselineResultsPerGFA = useResultsPerGFA(baselineResults);

  const co2ePerGFA = getConversionFactorValue(baselineResultsPerGFA, 'co2e');
  const costPerGFA = getConversionFactorValue(baselineResultsPerGFA, 'sek');
  const diffValue = hasVisibleProposalTarget(target)
    ? co2ePerGFA - target
    : undefined;

  const costBarHeight = useMemo(
    () =>
      displayCurrency.isEnabled && costPerGfaMax > 0
        ? (costPerGFA / costPerGfaMax) * maxBarHeight
        : 0,
    [displayCurrency.isEnabled, costPerGFA, costPerGfaMax, maxBarHeight],
  );

  const costLabel = useMemo(
    () =>
      displayCurrency.isEnabled && costPerGFA > 0
        ? getDisplayValueData(
            { value: costPerGFA, unit: 'sek' },
            { outputUnit: displayCurrency.current, notation: 'compact' },
          ).value
        : undefined,
    [displayCurrency, costPerGFA],
  );

  const co2eChartData = useMemo(
    () => getCo2eChartDataFromResults(baselineResultsPerGFA),
    [baselineResultsPerGFA],
  );

  const handleClick = useMouseEventCallback(
    () => {
      // Shared / view-only: switch to baseline view; edits stay behind readonly elsewhere.
      if (isSelected) {
        onDeselectGroup?.();
        // Second tap on baseline (already no proposal) clears element selection — mirrors active proposal re-click (DEV-3201).
        if (hasElementSelection(selectedElementId)) {
          void navigateTo({
            page: elementListUrlPageFromPage(pageFromUrl),
            elementId: URL_CATEGORY_NONE,
          });
        }
        return;
      }
      setSelectedProposalId(undefined);
      applyElementSelectionForProposalNavigation(
        resolveElementSelectionForProposalNavigation(
          version,
          undefined,
          selectedElementId,
        ),
        navigateTo,
        elementListUrlPageFromPage(pageFromUrl),
      );
    },
    { ignoreInputEvents: true },
  );

  return (
    <OverviewBarShell
      barWidth={barWidth}
      maxBarHeight={maxBarHeight}
      color={PROPOSALS_OVERVIEW_BASELINE_BAR_COLOR}
      co2eChartData={co2eChartData}
      co2ePerGFA={co2ePerGFA}
      co2ePerGfaMax={co2ePerGfaMax}
      costBarHeight={costBarHeight}
      costLabel={costLabel}
      showInnerBorders
      innerBorderColor={PROPOSALS_OVERVIEW_BASELINE_BAR_COLOR}
      showCo2LabelIcon={showCo2LabelIcon}
      tooltipPlacement="right"
      diffValue={diffValue}
      active={isSelected}
      isSelected={isSelected}
      onClick={handleClick}
      name="Baseline"
      nameStyle="baseline"
    />
  );
};

export default BaselineBar;
